import {
  SkyBadge,
  SkyButton,
  SkyDataGrid,
  SkyDropdown,
  SkyInput,
  SkyLink,
  SkyText,
  SkyTooltip,
  type SkyDataGridColumn,
} from "@skypointcloud/skypoint-ui";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useLocation, useSearchParams } from "wouter";
import type {
  InstanceAdminView,
  InstanceStatus,
  MarkStorageRemovedResponse,
} from "@shared/types/instancesAdmin";
import { TopbarActionsPortal } from "@/components/navigation/TopbarActionsPortal";
import { DisableInstanceDialog } from "@/components/platform/DisableInstanceDialog";
import { EnableInstanceDialog } from "@/components/platform/EnableInstanceDialog";
import { MarkStorageRemovedDialog } from "@/components/platform/MarkStorageRemovedDialog";
import { EMPTY_CELL } from "@/components/platform/format";
import { InstanceDialog, type InstanceFormValues } from "@/components/platform/InstanceDialog";
import { ProvisioningPanel } from "@/components/platform/ProvisioningPanel";
import { RefreshErrorBar } from "@/components/states/RefreshErrorBar";
import { provisioningSummary } from "@/components/platform/provisioningSteps";
import {
  EmptyState,
  ErrorState,
  GridSkeleton,
  ScreenNote,
} from "@/components/platform/ScreenState";
import {
  INSTANCES_ADMIN_QUERY_KEY,
  isTerminalProvisioning,
  useCreateInstance,
  useDisableInstance,
  useEnableInstance,
  useMarkStorageRemoved,
  useInstancesAdmin,
  useProvisioningJob,
  useUpdateInstance,
} from "@/hooks/use-instances-admin";
import { useNotice } from "@/lib/feedback/NoticeProvider";
import { reportFailureShown } from "@/lib/feedback/failure";
import { usePermissions } from "@/hooks/use-permissions";
import { tenantGap, useTenants, type TenantGap } from "@/hooks/use-tenants";
import {
  AddIcon,
  DefinitionIcon,
  EditIcon,
  InstancesIcon,
  TenantsIcon,
  ViewIcon,
} from "@/lib/icons";
import { instancePath } from "@/lib/instancePath";
import { NEW_TENANT_ADDRESS, ROUTES, absolutePath } from "@/lib/routes";
import { LIST_GRID_PAGINATION, actionsColumnWhen } from "@/lib/grid";
import { formatCalendarDay } from "@/lib/time/displayTime";

/**
 * Instances (FR-20, FR-22, canvas 12).
 *
 * Two roles reach this screen and they see different things, enforced on the
 * server as a SQL predicate rather than as a filter here: a TenantAdmin holding
 * `tenant.instances.manage` lists the instances of the tenants they administer,
 * and an instance outside that set answers 404 exactly as one that does not
 * exist, so ids cannot be enumerated. Creating and disabling need
 * `platform.instances.manage`, so a TenantAdmin reads this screen and does not
 * write to it — the buttons are hidden AND the routes refuse them.
 */

const STATUS_FILTER_OPTIONS = [
  { value: "all", text: "All statuses" },
  { value: "active", text: "Active" },
  { value: "provisioning", text: "Provisioning" },
  { value: "disabled", text: "Disabled" },
];

/**
 * The status cell for one row.
 *
 * A provisioning row reads its own job so the grid alone says how far along it
 * is ("Provisioning 3/5"). It is a component rather than an inline render so it
 * can hold the poll: only provisioning rows subscribe, and the panel below
 * shares the same query key, so opening it costs no second request.
 */
function InstanceStatusCell({
  instance,
  canReadJob,
}: {
  instance: InstanceAdminView;
  /** The job route is platform scope (B-10): a TenantAdmin gets a plain badge and no poll. */
  canReadJob: boolean;
}) {
  const isProvisioning = instance.status === "provisioning";
  const job = useProvisioningJob(isProvisioning && canReadJob ? instance.id : null);
  // #930: a job already finished when this row first read it (it ended between the list's read
  // and the first poll) never moves to a terminal state under the hook's watch, so the row reads
  // the list again itself, once per job, rather than saying "Provisioning 7/7" until a reload.
  const queryClient = useQueryClient();
  const reread = useRef<string | null>(null);
  const finishedJob =
    isProvisioning && isTerminalProvisioning(job.data?.status) ? (job.data?.id ?? null) : null;
  useEffect(() => {
    if (finishedJob === null || reread.current === finishedJob) return;
    reread.current = finishedJob;
    void queryClient.invalidateQueries({ queryKey: INSTANCES_ADMIN_QUERY_KEY });
  }, [finishedJob, queryClient]);

  if (!isProvisioning) {
    return (
      <SkyBadge appearance="tint" color={instance.status === "active" ? "success" : "subtle"}>
        {instance.status === "active" ? "Active" : "Disabled"}
      </SkyBadge>
    );
  }

  return (
    <SkyBadge appearance="tint" color="informative" testId={`instance-status-${instance.slug}`}>
      {canReadJob ? provisioningSummary(job.data) : "Provisioning"}
    </SkyBadge>
  );
}

/**
 * Where the instance's data lives (FR-22; ADR 0005: its own schema in its tenant's database), on
 * an info icon (round 6 walk W17: the Database column wrapped and repeated what Edit shows). The
 * words stay in the DOM, `sr-only`, so a screen reader and a search of the page still find them;
 * the trigger passes `aria-describedby={undefined}` so they are not read twice while it is open.
 *
 * #1191: only a PlatformAdmin's answer carries the placement (the server leaves it out for a
 * TenantAdmin), so with none there is no icon at all.
 */
function InstanceDatabaseHint({ instance }: { instance: InstanceAdminView }) {
  if (instance.tenantDbName === undefined || instance.tenantDbServer === undefined) return null;
  const where = [
    `Database ${instance.tenantDbName}`,
    ...(instance.tenantDbSchema ? [`schema ${instance.tenantDbSchema}`] : []),
    `on ${instance.tenantDbServer}`,
  ].join(", ");
  return (
    <SkyTooltip content={where}>
      <span
        tabIndex={0}
        aria-describedby={undefined}
        className="inline-flex shrink-0 items-center text-muted-foreground"
        data-testid={`instance-database-${instance.slug}`}
      >
        <DefinitionIcon className="size-3.5" aria-hidden="true" />
        <span className="sr-only">
          Database <span>{instance.tenantDbName}</span>
          {instance.tenantDbSchema ? (
            <>
              {/* copy-jargon-ok: where the row's data lives, the support detail behind the icon. */}
              , schema{" "}
              <span data-testid={`instance-schema-${instance.slug}`}>
                {instance.tenantDbSchema}
              </span>
            </>
          ) : null}
          , on <span>{instance.tenantDbServer}</span>
        </span>
      </span>
    </SkyTooltip>
  );
}

/** #921: said where an instance cannot be created because there is no tenant at all. */
export const NO_TENANT_FOR_INSTANCE =
  "Instances live in a tenant, and there is no tenant yet. Create a tenant first. Its first instance is created with it.";

/** #921 review: said when tenants exist but every one is disabled. */
export const NO_ACTIVE_TENANT_FOR_INSTANCE =
  "Instances live in a tenant, and every tenant is disabled. Enable one on Tenants to add an instance.";

/**
 * No instance to list: none matches the filters, none exists yet (New instance), or no tenant can
 * hold one (#921: tenant first, then instance): with none at all it links to New tenant, with only
 * disabled ones to Tenants, where one is enabled.
 */
function NoInstances({
  isFiltered,
  onCreate,
  gap,
}: {
  isFiltered: boolean;
  /** New instance, for a platform admin only. */
  onCreate: (() => void) | undefined;
  gap: TenantGap;
}) {
  const [, navigate] = useLocation();
  if (gap !== null && !isFiltered) {
    const none = gap === "none";
    return (
      <EmptyState
        testId="instances-no-tenant"
        icon={TenantsIcon}
        title={none ? "No tenant yet" : "No active tenant"}
        message={none ? NO_TENANT_FOR_INSTANCE : NO_ACTIVE_TENANT_FOR_INSTANCE}
        action={
          <SkyButton
            appearance="primary"
            icon={none ? AddIcon : TenantsIcon}
            onClick={() => navigate(absolutePath(none ? NEW_TENANT_ADDRESS : ROUTES.adminTenants))}
            testId="instances-create-tenant"
          >
            {none ? "Create a tenant" : "Open Tenants"}
          </SkyButton>
        }
      />
    );
  }
  return (
    <EmptyState
      testId="instances-empty"
      icon={InstancesIcon}
      title={isFiltered ? "No instances match those filters" : "No instances yet"}
      message="An instance is where connections, schedules and runs live. Create one to start."
      action={
        onCreate && !isFiltered ? (
          <SkyButton appearance="primary" icon={AddIcon} onClick={onCreate}>
            New instance
          </SkyButton>
        ) : null
      }
    />
  );
}

/**
 * #1294: the instance's name opens it, on its Overview, as choosing it in the user menu does: the
 * address names the instance (`/i/<id>/overview`), and the shell selects it while `GET /me` lists
 * it, so a PlatformAdmin moves to another tenant's instance in one click. A disabled instance
 * cannot be opened, and neither can an active instance of a disabled (or deleted) tenant, which
 * `GET /me` leaves out (`isInstanceDisabled`, `server/auth/accessResolver.ts`); either way the
 * name stays plain text with the reason under it. Only a PlatformAdmin sees the second kind (B-20).
 */
export const DISABLED_INSTANCE_NOT_OPENED = "Disabled, so it cannot be opened.";
export const DISABLED_TENANT_NOT_OPENED = "Its tenant is disabled, so it cannot be opened.";

/** Why this instance cannot be opened, or null when its name opens it. */
function notOpenedReason(instance: InstanceAdminView): string | null {
  if (instance.status === "disabled") return DISABLED_INSTANCE_NOT_OPENED;
  if (instance.tenantStatus === "disabled") return DISABLED_TENANT_NOT_OPENED;
  return null;
}

function InstanceName({ instance }: { instance: InstanceAdminView }) {
  const reason = notOpenedReason(instance);
  if (reason) {
    return (
      <>
        <SkyText size={300} weight="medium" block truncate title={instance.name}>
          {instance.name}
        </SkyText>
        <SkyText size={200} color="muted" block testId={`instance-not-opened-${instance.slug}`}>
          {reason}
        </SkyText>
      </>
    );
  }
  return (
    <div className="min-w-0 truncate">
      <SkyLink
        as={Link}
        href={absolutePath(instancePath(instance.id, ROUTES.overview))}
        title={instance.name}
        aria-label={`Open ${instance.name}, ${instance.tenantDisplayName}`}
        className="font-medium"
        testId={`instance-open-${instance.slug}`}
      >
        {instance.name}
      </SkyLink>
    </div>
  );
}

/** The instances. */
function InstancesGrid({
  rows,
  columns,
}: {
  rows: InstanceAdminView[];
  columns: SkyDataGridColumn<InstanceAdminView>[];
}) {
  return (
    <>
      <SkyDataGrid<InstanceAdminView>
        data={rows}
        columns={columns}
        {...LIST_GRID_PAGINATION}
        getRowId={(row) => row.id}
        testId="instances-grid"
      />
      {/* #1191: no count line under the grid (the pager says it) and no word of how a row
          refreshes (the note above says each row shows its progress). */}
    </>
  );
}

/**
 * The mark's success notice. "present" only says some archive day exists, so the notice names the
 * last day it reaches rather than promising the whole history is kept.
 */
function markedRemovedNotice(
  response: Pick<MarkStorageRemovedResponse, "archive" | "archiveThrough">,
): string {
  if (response.archive === "none") {
    return "Marked storage removed. Its audit history was never archived.";
  }
  return response.archiveThrough
    ? `Marked storage removed. Its audit history is archived through ${formatCalendarDay(response.archiveThrough)}.`
    : "Marked storage removed. Part of its audit history is archived.";
}

export default function AdminInstancesPage() {
  const notice = useNotice();
  // Each dialog on this screen prints its own failure inside it (`ErrorState` fed by the
  // mutation's `error`) and stays open, so a failure is only logged here (ux-s10 §3).
  const { can } = usePermissions();
  const canManagePlatform = can("platform.instances.manage");

  const instances = useInstancesAdmin();
  // Tenants are only needed to create and to filter, and both are platform
  // scope; a TenantAdmin asking for `/platform/tenants` would get a 403 the
  // screen would then have to explain away.
  const tenants = useTenants({ enabled: canManagePlatform });
  // #921: no tenant takes a new instance (none yet, or every one disabled), so the empty state
  // says which and where to go; the dialog says the same in place of an empty Tenant list.
  const gap: TenantGap =
    canManagePlatform && tenants.data !== undefined ? tenantGap(tenants.data) : null;

  const [searchTerm, setSearchTerm] = useState("");
  // wouter's own reader rather than `window.location.search`: the router is what
  // owns the address here, and reading around it sees a query string the router
  // has not applied yet on the first render after a navigation.
  const [searchParams] = useSearchParams();
  // `/admin/instances?tenant=<id>` is the link a tenant's Instances count sends, so the
  // filter starts on that tenant. It is the INITIAL value rather than an effect
  // that writes state: an effect would fight the dropdown, re-applying the
  // query string every time the user chose something else.
  const [tenantFilter, setTenantFilter] = useState(() => searchParams.get("tenant") ?? "all");
  const [statusFilter, setStatusFilter] = useState<"all" | InstanceStatus>("all");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<InstanceAdminView | null>(null);
  const [disabling, setDisabling] = useState<InstanceAdminView | null>(null);
  const [enabling, setEnabling] = useState<InstanceAdminView | null>(null);
  const [markingRemoved, setMarkingRemoved] = useState<InstanceAdminView | null>(null);
  const [watching, setWatching] = useState<InstanceAdminView | null>(null);

  const create = useCreateInstance();
  const update = useUpdateInstance();
  const disable = useDisableInstance();
  const enable = useEnableInstance();
  const markRemoved = useMarkStorageRemoved();

  const tenantOptions = useMemo(
    () => [
      { value: "all", text: "Tenant: all" },
      // The tenant's Display name (round 6 A3; T-04, owner 2026-10-02: "Tenant, Instance name").
      ...(tenants.data ?? []).map((tenant) => ({
        value: tenant.id,
        text: tenant.displayName,
      })),
    ],
    [tenants.data],
  );

  const rows = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return (instances.data ?? []).filter((instance) => {
      if (tenantFilter !== "all" && instance.tenantId !== tenantFilter) return false;
      if (statusFilter !== "all" && instance.status !== statusFilter) return false;
      if (term === "") return true;
      return (
        instance.name.toLowerCase().includes(term) ||
        instance.slug.toLowerCase().includes(term) ||
        instance.tenantName.toLowerCase().includes(term) ||
        instance.tenantDisplayName.toLowerCase().includes(term)
      );
    });
  }, [instances.data, searchTerm, tenantFilter, statusFilter]);

  function openCreate() {
    setEditing(null);
    create.reset();
    setFormOpen(true);
  }

  function openEdit(instance: InstanceAdminView) {
    setEditing(instance);
    update.reset();
    setFormOpen(true);
  }

  function saveInstance(values: InstanceFormValues) {
    if (editing) {
      update.mutate(
        {
          id: editing.id,
          body: {
            name: values.name,
            environmentLabel: values.environmentLabel === "" ? null : values.environmentLabel,
          },
        },
        {
          onSuccess: () => {
            setFormOpen(false);
            notice.success("Instance updated");
          },
          onError: (error) => reportFailureShown(error),
        },
      );
      return;
    }

    create.mutate(
      {
        tenantId: values.tenantId,
        name: values.name,
        slug: values.slug,
        destinationType: values.destinationType,
        ...(values.environmentLabel === "" ? {} : { environmentLabel: values.environmentLabel }),
      },
      {
        onSuccess: (response) => {
          setFormOpen(false);
          notice.success("Its row shows the progress and turns Active when it is ready.", {
            title: "Setting up the instance",
          });
          // The 202 carries the instance, so the panel opens on the job that was
          // just queued rather than waiting for the list to refetch.
          setWatching(response.instance);
        },
        onError: (error) => reportFailureShown(error),
      },
    );
  }

  function confirmDisable(reason: string) {
    if (!disabling) return;
    disable.mutate(
      { id: disabling.id, body: reason === "" ? {} : { reason } },
      {
        onSuccess: () => {
          notice.success("Instance disabled");
          setDisabling(null);
        },
        onError: (error) => reportFailureShown(error),
      },
    );
  }

  function confirmMarkRemoved(reason: string) {
    if (!markingRemoved) return;
    markRemoved.mutate(
      { id: markingRemoved.id, body: { reason } },
      {
        onSuccess: (response) => {
          notice.success(markedRemovedNotice(response));
          setMarkingRemoved(null);
        },
        onError: (error) => reportFailureShown(error),
      },
    );
  }

  function confirmEnable(reason: string) {
    if (!enabling) return;
    enable.mutate(
      { id: enabling.id, body: { reason } },
      {
        onSuccess: () => {
          notice.success(
            "Instance enabled. Its schedules stay off until someone enables each one.",
          );
          setEnabling(null);
        },
        onError: (error) => reportFailureShown(error),
      },
    );
  }

  const columns: SkyDataGridColumn<InstanceAdminView>[] = [
    {
      // Round 6 A3 and walk W17: the instance, its tenant once (its Display name;
      // round 5 printed "Contoso Retail · Contoso Retail"), and its slug as the subtitle. For a
      // PlatformAdmin the database is behind an info icon; the Edit dialog shows the whole
      // placement. The name opens the instance (#1294).
      key: "name",
      header: "Instance",
      sortable: true,
      width: canManagePlatform ? "46%" : "62%",
      renderCell: (_value, row) => (
        <div className="flex min-w-0 flex-col">
          <InstanceName instance={row} />
          <SkyText
            size={200}
            color="muted"
            block
            truncate
            title={row.tenantDisplayName}
            testId={`instance-tenant-${row.slug}`}
          >
            {row.tenantDisplayName}
          </SkyText>
          {row.tenantPurpose === "canary" ? (
            // #826: an instance of Skypoint's own daily canary tenant.
            <div className="flex items-start">
              <SkyBadge
                appearance="tint"
                color="informative"
                testId={`instance-canary-${row.slug}`}
              >
                Canary
              </SkyBadge>
            </div>
          ) : null}
          <div className="flex min-w-0 items-center gap-1">
            <SkyText
              size={200}
              color="muted"
              font="monospace"
              block
              truncate
              title={row.slug}
              testId={`instance-slug-${row.slug}`}
            >
              {row.slug}
            </SkyText>
            <InstanceDatabaseHint instance={row} />
          </div>
        </div>
      ),
    },
    {
      key: "environmentLabel",
      header: "Environment",
      width: canManagePlatform ? "16%" : "20%",
      renderCell: (_value, row) => (
        <SkyText size={200}>{row.environmentLabel ?? EMPTY_CELL}</SkyText>
      ),
    },
    {
      key: "status",
      header: "Status",
      sortable: true,
      width: canManagePlatform ? "16%" : "18%",
      renderCell: (_value, row) => (
        <InstanceStatusCell instance={row} canReadJob={canManagePlatform} />
      ),
    },
    // #1196: every action here needs platform.instances.manage, so a TenantAdmin's grid has no
    // Actions column at all (the guide: read only, no Edit, Disable, Enable or View job).
    ...actionsColumnWhen<InstanceAdminView>(canManagePlatform, {
      key: "actions",
      header: "Actions",
      width: "22%",
      renderCell: (_value, row) => (
        <div className="flex items-center gap-1">
          {canManagePlatform && (row.status === "provisioning" || row.provisionedAt === null) ? (
            <SkyButton
              appearance="subtle"
              size="small"
              icon={ViewIcon}
              onClick={() => setWatching(row)}
              aria-label={`View the provisioning job for ${row.name}`}
            >
              View job
            </SkyButton>
          ) : null}
          {canManagePlatform ? (
            <>
              <SkyButton
                appearance="subtle"
                size="small"
                icon={EditIcon}
                onClick={() => openEdit(row)}
                aria-label={`Edit ${row.name}`}
              >
                Edit
              </SkyButton>
              {row.status !== "disabled" ? (
                <SkyButton
                  appearance="subtle"
                  size="small"
                  onClick={() => setDisabling(row)}
                  aria-label={`Disable ${row.name}`}
                >
                  Disable
                </SkyButton>
              ) : (
                <SkyButton
                  appearance="subtle"
                  size="small"
                  onClick={() => setEnabling(row)}
                  aria-label={`Enable ${row.name}`}
                  testId="instance-enable"
                >
                  Enable
                </SkyButton>
              )}
              {row.status === "disabled" && !row.storageRemovedAt ? (
                <SkyButton
                  appearance="subtle"
                  size="small"
                  onClick={() => {
                    markRemoved.reset();
                    setMarkingRemoved(row);
                  }}
                  aria-label={`Mark ${row.name} storage removed`}
                  testId="instance-mark-storage-removed"
                >
                  Mark storage removed
                </SkyButton>
              ) : null}
            </>
          ) : null}
        </div>
      ),
    }),
  ];

  const isFiltered = searchTerm.trim() !== "" || tenantFilter !== "all" || statusFilter !== "all";

  function renderInstances(): ReactNode {
    if (instances.isPending) {
      return <GridSkeleton rows={4} label="Loading instances…" testId="instances-loading" />;
    }
    // #1398: only a failed first read replaces the list; a failed re-read keeps the rows.
    if (instances.isError && !instances.data) {
      return (
        <ErrorState
          title="The instance list did not load"
          error={instances.error}
          onRetry={() => void instances.refetch()}
          testId="instances-error"
        />
      );
    }
    const refreshError = instances.isError ? (
      <RefreshErrorBar
        things="instances"
        error={instances.error}
        onRetry={() => void instances.refetch()}
        testId="instances-refresh-error"
      />
    ) : null;
    if (rows.length === 0) {
      return (
        <>
          {refreshError}
          <NoInstances
            isFiltered={isFiltered}
            onCreate={canManagePlatform ? openCreate : undefined}
            gap={gap}
          />
        </>
      );
    }
    return (
      <>
        {refreshError}
        <InstancesGrid rows={rows} columns={columns} />
      </>
    );
  }

  return (
    <div className="flex flex-col gap-4" data-testid="instances-page">
      {canManagePlatform ? (
        <TopbarActionsPortal>
          <SkyButton appearance="primary" icon={AddIcon} onClick={openCreate} testId="new-instance">
            New instance
          </SkyButton>
        </TopbarActionsPortal>
      ) : null}

      <ScreenNote
        title="New instances are set up in the background"
        message="Each row shows its progress. Leaving this page does not stop it."
      />

      <div className="flex flex-wrap items-end gap-3">
        {canManagePlatform ? (
          <div className="w-64">
            <SkyDropdown
              label="Tenant"
              options={tenantOptions}
              selectedOptions={[tenantFilter]}
              onOptionSelect={(values) => setTenantFilter(values[0] ?? "all")}
              loading={tenants.isPending}
              testId="instance-tenant-filter"
            />
          </div>
        ) : null}
        <div className="w-72">
          <SkyInput
            label="Search instances"
            type="search"
            value={searchTerm}
            clearable
            onChange={(event) => setSearchTerm(event.target.value)}
            onClear={() => setSearchTerm("")}
            testId="instance-search"
          />
        </div>
        <div className="w-52">
          <SkyDropdown
            label="Status"
            options={STATUS_FILTER_OPTIONS}
            selectedOptions={[statusFilter]}
            onOptionSelect={(values) =>
              setStatusFilter((values[0] as "all" | InstanceStatus) ?? "all")
            }
            testId="instance-status-filter"
          />
        </div>
      </div>

      {renderInstances()}

      {watching ? (
        <ProvisioningPanel
          instanceId={watching.id}
          instanceName={watching.name}
          // The list's current row, not the snapshot taken when the panel opened: a Retry moves it.
          instance={(instances.data ?? []).find((entry) => entry.id === watching.id) ?? watching}
          onDismiss={() => setWatching(null)}
        />
      ) : null}

      <InstanceDialog
        key={`instance-${formOpen}-${editing?.id ?? "new"}`}
        open={formOpen}
        onOpenChange={setFormOpen}
        instance={editing}
        tenants={tenants.data ?? []}
        defaultTenantId={tenantFilter === "all" ? null : tenantFilter}
        onSubmit={saveInstance}
        isSaving={create.isPending || update.isPending}
        error={editing ? update.error : create.error}
      />

      <DisableInstanceDialog
        key={`disable-${disabling?.id ?? "none"}`}
        open={disabling !== null}
        onOpenChange={(open) => {
          if (!open) setDisabling(null);
        }}
        instance={disabling}
        onConfirm={confirmDisable}
        isSaving={disable.isPending}
        error={disable.error}
      />

      <EnableInstanceDialog
        key={`enable-${enabling?.id ?? "none"}`}
        open={enabling !== null}
        onOpenChange={(open) => {
          if (!open) setEnabling(null);
        }}
        instance={enabling}
        onConfirm={confirmEnable}
        isSaving={enable.isPending}
        error={enable.error}
      />

      {markingRemoved ? (
        // Mounted only while open: its preflight query belongs to one instance's opening.
        <MarkStorageRemovedDialog
          key={`storage-removed-${markingRemoved.id}`}
          open
          onOpenChange={(open) => {
            if (!open) setMarkingRemoved(null);
          }}
          instance={markingRemoved}
          onConfirm={confirmMarkRemoved}
          isSaving={markRemoved.isPending}
          error={markRemoved.error}
        />
      ) : null}
    </div>
  );
}
